import { getToken } from "@/api/client";
import type { Attachment } from "@/api/content";

/**
 * File attachment endpoints. These use `fetch` directly rather than the JSON
 * client: uploads send multipart/form-data and downloads return binary blobs.
 * Auth is a Bearer token (not a cookie), so a bare <img>/<embed src> can't load a
 * protected file — we fetch the bytes with the header and hand back an object URL.
 */
function authHeaders(): Record<string, string> {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export async function uploadFile(moduleId: string, file: File): Promise<Attachment> {
  const form = new FormData();
  form.append("file", file);
  const res = await fetch(`/api/modules/${moduleId}/files`, {
    method: "POST",
    headers: authHeaders(), // do NOT set Content-Type; the browser adds the boundary
    body: form,
  });
  if (!res.ok) {
    const msg = await res.text();
    try {
      throw new Error(JSON.parse(msg)?.error?.message || "Upload mislukt");
    } catch {
      throw new Error("Upload mislukt");
    }
  }
  return res.json();
}

/** Fetch a protected file as a blob and return an object URL (revoke when done). */
export async function fetchFileObjectUrl(fileId: string): Promise<string> {
  const res = await fetch(`/api/files/${fileId}`, { headers: authHeaders() });
  if (!res.ok) throw new Error("Kon bestand niet laden");
  return URL.createObjectURL(await res.blob());
}

export async function deleteFile(fileId: string): Promise<void> {
  const res = await fetch(`/api/files/${fileId}`, { method: "DELETE", headers: authHeaders() });
  if (!res.ok && res.status !== 204) throw new Error("Verwijderen mislukt");
}
